import React, {useContext, useEffect, useMemo, useState} from 'react';
import MUIDataTable from "mui-datatables";
import clsx from 'clsx';
// import {Tooltip} from "@material-ui/core"; // used by the declined "+N" group overflow
import {fade, makeStyles, MuiThemeProvider, useTheme} from "@material-ui/core/styles";
// import ChevronRightIcon from '@material-ui/icons/ChevronRight'; // chevron column declined
import {useSnackbar} from 'notistack';
import Papa from "papaparse";
import {saveAs} from "file-saver";
import JString from "@easytech-international-sdn-bhd/jstring";
import {de, getSetting, isPromoter} from "../../Helper/Helper";
import AppContext from "../../Services/AppContext";
import PersistantData from "../../Services/PersistantData";
import API_Service, {axiosDefaultConfig, axiosURIencode} from "../../Services/API_Service";
import {req_user_list, req_module_setting} from "../../Helper/Constants";
import UserSettingsDetails from "../../Components/UserSettingDetails";
import LinearLoading from "../../Components/LinearLoading";
import UserTableToolbar, {ROLE_FILTER_ALL} from "./components/users/UserTableToolbar";
import {ROLE_NONE, resolveRoleKey, roleLabel, UserRolePill} from "./components/users/UserRolePill";
import {SKELETON_COLUMNS, UserTableEmpty, UserTableSkeleton} from "./components/users/UserTableStates";
import {buildUserTableTheme} from "./components/users/userTableTheme";

// const GROUP_VISIBLE_MAX = 3; // group overflow cap, declined: show all members
const SEARCH_DEBOUNCE_MS = 200;
const OPEN_COLUMN = '__open';
const EXPORT_LABELS = {
    login: 'Login',
    name: 'Name',
    role_name: 'Role',
    group: 'Group',
    email: 'Email',
    contact_no: 'Contact No.',
    company: 'Branch'
};

const useStyles = makeStyles((theme) => ({
    '@keyframes rise': {
        from: {opacity: 0, transform: 'translateY(6px)'},
        to: {opacity: 1, transform: 'none'}
    },
    root: {
        display: 'flex',
        flexDirection: 'column'
    },
    tableWrap: {
        position: 'relative'
    },
    loadingStrip: {
        position: 'absolute',
        top: 44,
        left: 1,
        right: 1,
        zIndex: 2
    },
    refreshing: {
        '& tbody': {
            opacity: 0.55,
            transition: theme.transitions.create('opacity', {duration: 150})
        }
    },
    row: {
        animation: '$rise 240ms cubic-bezier(0.22, 1, 0.36, 1) both',
        '&:nth-child(2)': {animationDelay: '30ms'},
        '&:nth-child(3)': {animationDelay: '60ms'},
        '&:nth-child(4)': {animationDelay: '90ms'},
        '&:nth-child(5)': {animationDelay: '120ms'},
        '&:nth-child(6)': {animationDelay: '150ms'},
        '&:nth-child(7)': {animationDelay: '180ms'},
        '&:nth-child(8)': {animationDelay: '210ms'}
        // chevron column declined
        // '&:hover $chevron': {
        //     color: theme.palette.text.primary,
        //     transform: 'translateX(3px)'
        // }
    },
    nameCell: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        minWidth: 0
    },
    name: {
        fontSize: 14,
        fontWeight: 600,
        color: theme.palette.text.primary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    secondary: {
        fontSize: 13,
        color: theme.palette.text.secondary,
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap'
    },
    dash: {
        fontSize: 13,
        color: theme.palette.text.disabled
    },
    groupCell: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 4,
        padding: '6px 0'
    },
    // "+N" overflow pill, declined with the group cap
    // more: {
    //     height: 22,
    //     padding: '0 8px',
    //     borderRadius: 999,
    //     display: 'inline-flex',
    //     alignItems: 'center',
    //     fontSize: 11,
    //     fontWeight: 700,
    //     backgroundColor: fade(theme.palette.text.primary, 0.06),
    //     color: theme.palette.text.secondary,
    //     cursor: 'default'
    // },
    tag: {
        display: 'inline-block',
        padding: '3px 7px',
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        backgroundColor: fade(theme.palette.text.primary, 0.05),
        color: theme.palette.text.secondary
    },
    // chevron column declined
    // chevron: {
    //     display: 'block',
    //     color: theme.palette.text.disabled,
    //     transition: theme.transitions.create(['transform', 'color'], {
    //         duration: 160,
    //         easing: 'cubic-bezier(0.22, 1, 0.36, 1)'
    //     })
    // },
    // chevronCell: {
    //     width: 32,
    //     paddingLeft: '0 !important',
    //     paddingRight: '12px !important'
    // }
}));

export default function UserSettings() {
    const [gState] = useContext(AppContext);
    const classes = useStyles();
    const theme = useTheme();
    const {enqueueSnackbar} = useSnackbar();
    const singleton = PersistantData.getInstance();
    var {translation: language} = gState;
    if (typeof (language) === 'undefined') {
        language = singleton.getlang();
    }

    useEffect(() => {
        if (JString(gState.client).isValid()) {
            var axiosGetConfig = axiosDefaultConfig(gState.client);
            loadConfig(gState.client);
            loadUsers(axiosGetConfig, true);
        }
    }, [gState.client]);

    const [usersList, setUsersList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [userDDialogShow, setUDDialogShow] = useState(false);
    const [selectedUserDetails, setSelectedUD] = useState({});
    const [branchColumnEnabled, setBranchColumnEnabled] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [query, setQuery] = useState('');
    const [roleFilter, setRoleFilter] = useState(ROLE_FILTER_ALL);
    const [hiddenColumns, setHiddenColumns] = useState([]);
    const [sort, setSort] = useState({name: '', direction: 'none'});

    useEffect(() => {
        const timer = setTimeout(() => setQuery(searchInput.trim().toLowerCase()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const tableTheme = useMemo(() => buildUserTableTheme(theme), [theme]);

    const isHidden = (name) => hiddenColumns.indexOf(name) >= 0;

    const headCells = useMemo(() => {
        const isHidden = (name) => hiddenColumns.indexOf(name) >= 0;
        const sortDirectionOf = (name) => (sort.name === name ? sort.direction : 'none');
        const dash = <span className={classes.dash}>-</span>;
        const cols = [
            {
                name: "login",
                label: language.login,
                options: {
                    display: !isHidden('login'),
                    sortDirection: sortDirectionOf('login'),
                    filterType: 'textField',
                    customBodyRender: (value) => (<span className={classes.secondary}>{value}</span>)
                }
            },
            {
                name: "name",
                label: language.name,
                options: {
                    display: !isHidden('name'),
                    sortDirection: sortDirectionOf('name'),
                    filterType: 'textField',
                    customBodyRender: (value) => {
                        // Initials avatar was declined; name only.
                        // const roleIdx = cols.findIndex((c) => c.name === 'role_name');
                        // const posIdx = cols.findIndex((c) => c.name === 'position');
                        // const roleKey = resolveRoleKey(tableMeta.rowData[roleIdx], tableMeta.rowData[posIdx]);
                        return (
                            <span className={classes.nameCell}>
                                {/* <UserAvatar name={value} roleKey={roleKey}/> */}
                                <span className={classes.name}>{value}</span>
                            </span>
                        );
                    }
                }
            },
            {
                name: "role_name",
                label: language.user_role,
                options: {
                    display: !isHidden('role_name'),
                    sortDirection: sortDirectionOf('role_name'),
                    filterType: 'textField',
                    customBodyRender: (value, tableMeta) => {
                        const posIdx = cols.findIndex((c) => c.name === 'position');
                        const roleKey = resolveRoleKey(value, tableMeta.rowData[posIdx]);
                        if (roleKey === ROLE_NONE) {
                            return dash;
                        }
                        return (<UserRolePill roleKey={roleKey} label={roleLabel(roleKey, value, language)}/>);
                    }
                }
            },
            {
                name: "group",
                label: language.group,
                options: {
                    display: !isHidden('group'),
                    sort: false,
                    filterType: 'textField',
                    customBodyRender: (value) => {
                        if (!Array.isArray(value) || value.length === 0) {
                            return dash;
                        }
                        // Cap at GROUP_VISIBLE_MAX with a "+N" tooltip was declined; show all members, wrapping.
                        // const visible = value.slice(0, GROUP_VISIBLE_MAX);
                        // const rest = value.slice(GROUP_VISIBLE_MAX);
                        return (
                            <span className={classes.groupCell}>
                                {
                                    value.map((member, idx) => (
                                        <UserRolePill
                                            key={'grp-' + idx}
                                            small
                                            roleKey={resolveRoleKey(member.role, member.position)}
                                            label={member.name}
                                        />
                                    ))
                                }
                                {/*
                                    rest.length > 0 && (
                                        <Tooltip title={rest.map((member) => member.name).join(', ')} arrow>
                                            <span className={classes.more}>+{rest.length}</span>
                                        </Tooltip>
                                    )
                                */}
                            </span>
                        );
                    }
                }
            },
            {
                name: "email",
                label: language.email,
                options: {
                    display: !isHidden('email'),
                    sortDirection: sortDirectionOf('email'),
                    filterType: 'textField',
                    customBodyRender: (value) => (value === '-' ? dash : <span className={classes.secondary}>{value}</span>)
                }
            },
            {
                name: "contact_no",
                label: language.contact_no,
                options: {
                    display: !isHidden('contact_no'),
                    sortDirection: sortDirectionOf('contact_no'),
                    filterType: 'textField',
                    customBodyRender: (value) => (value === '-' ? dash : <span className={classes.secondary}>{value}</span>)
                }
            },
            {
                name: "position",
                label: language.position,
                options: {
                    display: false,
                    viewColumns: false,
                    filter: false
                }
            },
        ];
        if (branchColumnEnabled) {
            cols.push({
                name: "company",
                label: language.branch,
                options: {
                    display: !isHidden('company'),
                    sortDirection: sortDirectionOf('company'),
                    filterType: 'textField',
                    customBodyRender: (value) => (value ? <span className={classes.tag}>{value.toUpperCase()}</span> : '')
                }
            });
        }
        // Trailing chevron column was declined.
        // cols.push({
        //     name: OPEN_COLUMN,
        //     label: ' ',
        //     options: {
        //         display: true,
        //         sort: false,
        //         viewColumns: false,
        //         filter: false,
        //         download: false,
        //         setCellProps: () => ({className: classes.chevronCell}),
        //         setCellHeaderProps: () => ({className: classes.chevronCell}),
        //         customBodyRender: () => (<ChevronRightIcon className={classes.chevron}/>)
        //     }
        // });
        return cols;
    }, [language, classes, branchColumnEnabled, hiddenColumns, sort]);

    const decoratedUsers = useMemo(() => usersList.map((user) => {
        const roleKey = resolveRoleKey(user.role_name, user.position);
        const groupNames = Array.isArray(user.group) ? user.group.map((member) => member.name).join(' ') : '';
        const haystack = [
            user.login,
            user.name,
            roleLabel(roleKey, user.role_name, language),
            groupNames,
            user.email,
            user.contact_no,
            user.company
        ].filter(Boolean).join(' ').toLowerCase();
        return {user, roleKey, haystack};
    }), [usersList, language]);

    const roleCounts = useMemo(() => decoratedUsers.reduce((acc, {roleKey}) => {
        acc[roleKey] = (acc[roleKey] || 0) + 1;
        return acc;
    }, {[ROLE_FILTER_ALL]: decoratedUsers.length}), [decoratedUsers]);

    const filteredUsers = useMemo(() => {
        const rows = decoratedUsers
            .filter(({roleKey, haystack}) => (roleFilter === ROLE_FILTER_ALL || roleKey === roleFilter)
                && (query === '' || haystack.indexOf(query) >= 0))
            .map(({user}) => user);
        if (sort.name && sort.direction !== 'none') {
            const dir = sort.direction === 'desc' ? -1 : 1;
            rows.sort((a, b) => String(a[sort.name] || '').localeCompare(String(b[sort.name] || ''), undefined, {sensitivity: 'base'}) * dir);
        }
        return rows;
    }, [decoratedUsers, roleFilter, query, sort]);

    const columnOptions = headCells
        .filter((col) => col.name !== 'position' && col.name !== OPEN_COLUMN)
        .map((col) => ({name: col.name, label: col.label, visible: !isHidden(col.name)}));

    const toggleColumn = (name) => {
        setHiddenColumns((prev) => (prev.indexOf(name) >= 0 ? prev.filter((n) => n !== name) : [...prev, name]));
    };

    const clearFilters = () => {
        setSearchInput('');
        setQuery('');
        setRoleFilter(ROLE_FILTER_ALL);
    };

    const loadUsers = (axiosRequestConfig, initial = false) => {
        var parameters = '';
		if (gState.company) {
			const param = axiosURIencode({
				company: gState.company,
			});
			parameters = `?${param}`;
		}
        if (!initial) {
            setRefreshing(true);
        }
        API_Service.get(`${req_user_list}${parameters}`, axiosRequestConfig).then(({data}) => {
            const {result, extraData} = data;
            if (result) {
                var _data = [];
                for (let idx = 0; idx < extraData.length; idx++) {
                    var _arr = extraData[idx];
                    if (!_arr['email']) {
                        _arr['email'] = '-';
                    }
                    if (!_arr['contact_no']) {
                        _arr['contact_no'] = '-';
                    }
                    _data.push(_arr);
                }
                setUsersList(usersList => (_data));
            }
        }).catch((error) => {
            de(error);
            enqueueSnackbar(language.network_error, {variant: 'error'});
        }).finally(() => {
            setLoading(false);
            setRefreshing(false);
        });
    }

    const reloadUsers = () => {
        if (JString(gState.client).isValid()) {
            loadUsers(axiosDefaultConfig(gState.client));
        }
    };

    const loadConfig = (_client) => {
        API_Service.get(req_module_setting, axiosDefaultConfig(_client)).then(({data}) => {
            const {result, extraData} = data;
            if (result === 1) {
                const _config = getSetting(extraData, 'modules', {});
                if (_config['branch'] === true) {
                    setBranchColumnEnabled(true);
                }
            }
        }).catch((error) => {
            de(error);
            enqueueSnackbar(language.network_error, {variant: 'error'});
        });
    };

    const exportCSV = () => {
        if (usersList.length === 0) {
            return;
        }
        const csvData = usersList.map((user) => {
            const row = {};
            headCells.forEach((col) => {
                if (col.name === 'position' || col.name === OPEN_COLUMN) {
                    return;
                }
                const value = user[col.name];
                const label = EXPORT_LABELS[col.name] || col.label;
                switch (col.name) {
                    case 'group':
                        row[label] = Array.isArray(value) && value.length > 0
                            ? value.map((g) => g.name).join(', ')
                            : '-';
                        break;
                    case 'role_name':
                        row[label] = isPromoter(user.position) ? language.promoter : ((value === 'Manager' || value === 'Agent') ? value : '-');
                        break;
                    case 'company':
                        row[label] = value ? value.toUpperCase() : '';
                        break;
                    default:
                        row[label] = value || '-';
                        break;
                }
            });
            return row;
        });
        const csv = Papa.unparse(csvData);
        const blob = new Blob(['﻿' + csv], {type: 'text/csv;charset=utf-8;'});
        const dateStr = new Date().toISOString().slice(0, 10);
        saveAs(blob, `user_settings_report_${dateStr}.csv`);
    };

    const hasActiveFilter = query !== '' || roleFilter !== ROLE_FILTER_ALL;

    const tableOptions = useMemo(() => ({
        selectableRows: 'none',
        print: false,
        filter: false,
        download: false,
        search: false,
        viewColumns: false,
        elevation: 0,
        rowsPerPage: 150,
        rowsPerPageOptions: [150, 300, 600, 900],
        responsive: 'scrollFullHeight',
        rowHover: true,
        setTableProps: () => ({size: 'small'}),
        setRowProps: () => ({className: classes.row}),
        textLabels: {
            body: {
                noMatch: (<UserTableEmpty language={language} showClear={hasActiveFilter} onClear={clearFilters}/>)
            },
            pagination: {
                rowsPerPage: language.rows_per_page
            }
        },
        onColumnSortChange: (name, direction) => {
            setSort({name, direction: String(direction).indexOf('desc') === 0 ? 'desc' : 'asc'});
        },
        onRowClick: (rowData, {dataIndex}) => {
            setUDDialogShow(userDDialogShow => (true));
            setSelectedUD(selectedUserDetails => (filteredUsers[dataIndex]));
        },
    }), [classes.row, language, hasActiveFilter, filteredUsers]);

    return(
        <div className={classes.root}>
            <UserTableToolbar
                language={language}
                total={usersList.length}
                shown={filteredUsers.length}
                roleFilter={roleFilter}
                roleCounts={roleCounts}
                onRoleFilterChange={setRoleFilter}
                searchValue={searchInput}
                onSearchChange={setSearchInput}
                columnOptions={columnOptions}
                onToggleColumn={toggleColumn}
                // onRefresh={reloadUsers}   manual refresh declined
                // refreshing={refreshing}
                onExport={exportCSV}
                exportDisabled={usersList.length === 0}
            />

            {
                loading ? (
                    <UserTableSkeleton columns={SKELETON_COLUMNS}/>
                ) : (
                    <div className={clsx(classes.tableWrap, refreshing && classes.refreshing)}>
                        {refreshing && (<div className={classes.loadingStrip}><LinearLoading/></div>)}
                        <MuiThemeProvider theme={tableTheme}>
                            <MUIDataTable
                                title=""
                                data={filteredUsers}
                                columns={headCells}
                                options={tableOptions}
                            />
                        </MuiThemeProvider>
                    </div>
                )
            }

            <UserSettingsDetails
                open={userDDialogShow}
                setClose={() => {
                    setUDDialogShow(() => (false));
                    reloadUsers();
                }}
                selectedUserDetails={selectedUserDetails}
                language={language}
                usersList={usersList}
            />
        </div>
    )
}
